import { useQuery } from '@tanstack/react-query'
import api from '@/api/axiosApi'
import type { UserRead } from '@/api/shares'

interface PaginationMeta {
  skip: number
  limit: number
  total: number
  has_more: boolean
}

interface PaginatedUsersResponse {
  data: UserRead[]
  pagination: PaginationMeta
}

interface ManagerSearchParams {
  search?: string
  /** Roles eligible as reviewers (default MANAGER); one request per role. */
  roles?: string[]
}

/**
 * Hook to fetch managers for reviewer selection.
 * Uses the /admin/users endpoint with role=MANAGER filter.
 */
export function useManagerSearch(params: ManagerSearchParams = {}) {
  const { search, roles = ['MANAGER'] } = params

  return useQuery({
    queryKey: ['managers', search, roles],
    queryFn: async () => {
      const pages = await Promise.all(
        roles.map(role =>
          api.get<PaginatedUsersResponse>('/admin/users', {
            params: { role, search: search || undefined, limit: 100 },
          })
        )
      )
      const users = new Map<string, UserRead>()
      pages.forEach(page =>
        page.data.data.forEach(user => users.set(String(user.id), user))
      )
      return [...users.values()]
    },
  })
}
